import React from "react";
import { Navigate } from "react-router-dom";

/**
 * Rotas do produto F1 apenas (LAC-011 / DOC 09 § 4.1 · § 6).
 * Demos Velzon ficam em pages/ só como referência — **não** montar no router.
 */

// Auth tenant
import Login from "../pages/Authentication/Login";
import ForgetPasswordPage from "../pages/Authentication/ForgetPassword";
import Logout from "../pages/Authentication/Logout";
import Register from "../pages/Authentication/Register";
import AcceptInvite from "../pages/Authentication/AcceptInvite";
import AcceptOwnerInvite from "../pages/Authentication/AcceptOwnerInvite";
import LoginMfa from "../pages/Authentication/LoginMfa";
import GoogleOnboarding from "../pages/Authentication/GoogleOnboarding";
import PublicUnsubscribe from "../pages/Public/Unsubscribe";

// Tenant F1
import TenantHome from "../pages/Dashboard/TenantHome";
import ContactsList from "../pages/Audience/ContactsList";
import ImportContacts from "../pages/Audience/ImportContacts";
import BlocklistPage from "../pages/Audience/Blocklist";
import InactiveContactsPage from "../pages/Audience/InactiveContacts";
import TagsList from "../pages/Audience/TagsList";
import SegmentsList from "../pages/Audience/SegmentsList";
import FormList from "../pages/Audience/Forms/FormList";
import FormCreateEdit from "../pages/Audience/Forms/FormCreateEdit";
import FormEmbed from "../pages/Audience/Forms/FormEmbed";
import WelcomeAutomationPage from "../pages/Content/Automations/WelcomeAutomationPage";
import WinBackAutomationPage from "../pages/Content/Automations/WinBackAutomationPage";
import AbandonedCartAutomationPage from "../pages/Content/Automations/AbandonedCartAutomationPage";
import CampaignsList from "../pages/Content/Campaigns/CampaignsList";
import CampaignForm from "../pages/Content/Campaigns/CampaignForm";
import CampaignSendPage from "../pages/Content/Campaigns/CampaignSendPage";
import TemplatesList from "../pages/Content/Templates/TemplatesList";
import TemplateForm from "../pages/Content/Templates/TemplateForm";
import PlatformTemplatesGallery from "../pages/Content/Templates/PlatformTemplatesGallery";
import SendsList from "../pages/Sending/SendsList";
import SendDetail from "../pages/Sending/SendDetail";
import SendingSetupPage from "../pages/Tenant/Settings/SendingSetup";
import SendingControlsPage from "../pages/Tenant/Settings/SendingControls";
import DeliverabilityHubPage from "../pages/Tenant/Settings/Deliverability";
import SubscriptionSettingsPage from "../pages/Tenant/Settings/Subscription";
import AccountSettingsPage from "../pages/Tenant/Settings/Account";
import TeamSettingsPage from "../pages/Tenant/Settings/Team";
import WorkspaceSettingsPage from "../pages/Tenant/Settings/Workspace";
import ReferralSettingsPage from "../pages/Tenant/Settings/Referral";
import RssFeedsList from "../pages/Content/RssFeeds/RssFeedsList";
import ContactFieldsPage from "../pages/Tenant/Settings/ContactFields";
import ApiTokensPage from "../pages/Tenant/Settings/ApiTokens";
import WebhookList from "../pages/Tenant/Settings/Webhooks/WebhookList";
import WebhookCreateEdit from "../pages/Tenant/Settings/Webhooks/WebhookCreateEdit";
import IntegrationsHub from "../pages/Tenant/Integrations/IntegrationsHub";
import VtexIntegrationPage from "../pages/Tenant/Integrations/VtexIntegrationPage";
import ShopifyIntegrationPage from "../pages/Tenant/Integrations/ShopifyIntegrationPage";
import UappiIntegrationPage from "../pages/Tenant/Integrations/UappiIntegrationPage";
import SitePixelPage from "../pages/Tenant/Integrations/SitePixelPage";
import SuspendedBillingPage from "../pages/Tenant/Billing/Suspended";

// Super Admin
import SuperAdminLogin from "../pages/SuperAdmin/Auth/Login";
import SuperAdminDashboard from "../pages/SuperAdmin/Dashboard";
import SuperAdminPlansList from "../pages/SuperAdmin/Plans";
import SuperAdminCouponsList from "../pages/SuperAdmin/Coupons";
import SuperAdminPayPalSettings from "../pages/SuperAdmin/Billing/PayPalSettings";
import SuperAdminSending from "../pages/SuperAdmin/Sending";
import SuperAdminReputation from "../pages/SuperAdmin/Reputation";
import SuperAdminSesWebhooks from "../pages/SuperAdmin/SesWebhooks";
import SuperAdminInactiveRanking from "../pages/SuperAdmin/InactiveRanking";
import SuperAdminEmailSuppressions from "../pages/SuperAdmin/EmailSuppressions";
import SuperAdminDomainSuppressions from "../pages/SuperAdmin/DomainSuppressions";
import SuperAdminTenantsList from "../pages/SuperAdmin/Tenants";
import SuperAdminTenantNew from "../pages/SuperAdmin/Tenants/New";
import SuperAdminTenantDetail from "../pages/SuperAdmin/Tenants/Detail";
import PlatformTemplatesList from "../pages/SuperAdmin/PlatformTemplates/PlatformTemplatesList";
import PlatformTemplateForm from "../pages/SuperAdmin/PlatformTemplates/PlatformTemplateForm";
import SuperAdminSesEndpoints from "../pages/SuperAdmin/Ses/Endpoints";
import SuperAdminSesSetupGuide from "../pages/SuperAdmin/Ses/SetupGuide";
import SuperAdminSesByoGuide from "../pages/SuperAdmin/Ses/ByoSetupGuide";
import SuperAdminDnsNsGuide from "../pages/SuperAdmin/Dns/NsGuide";
import SuperAdminAccount from "../pages/SuperAdmin/Account";
import SuperAdminActivityLogs from "../pages/SuperAdmin/ActivityLogs";
import SuperAdminLegacyMigration from "../pages/SuperAdmin/LegacyMigration";
import NotFound from "../pages/Errors/NotFound";

const authProtectedRoutes = [
  { path: "/dashboard", component: <TenantHome /> },
  { path: "/index", component: <TenantHome /> },
  { path: "/contacts/import", component: <ImportContacts /> },
  { path: "/contacts/blocklist", component: <BlocklistPage /> },
  { path: "/contacts/inactive", component: <InactiveContactsPage /> },
  { path: "/contacts", component: <ContactsList /> },
  { path: "/tags", component: <TagsList /> },
  { path: "/segments", component: <SegmentsList /> },
  { path: "/forms/new", component: <FormCreateEdit /> },
  { path: "/forms/:id/embed", component: <FormEmbed /> },
  { path: "/forms/:id", component: <FormCreateEdit /> },
  { path: "/forms", component: <FormList /> },
  { path: "/automations/welcome", component: <WelcomeAutomationPage /> },
  { path: "/automations/win-back", component: <WinBackAutomationPage /> },
  { path: "/automations/abandoned-cart", component: <AbandonedCartAutomationPage /> },
  { path: "/campaigns/new", component: <CampaignForm /> },
  { path: "/campaigns/:id/send", component: <CampaignSendPage /> },
  { path: "/campaigns/:id/edit", component: <CampaignForm /> },
  { path: "/campaigns", component: <CampaignsList /> },
  { path: "/sends/:id", component: <SendDetail /> },
  { path: "/sends", component: <SendsList /> },
  { path: "/rss-feeds", component: <RssFeedsList /> },
  { path: "/templates/new", component: <TemplateForm /> },
  { path: "/templates/gallery", component: <PlatformTemplatesGallery /> },
  { path: "/templates/:id/edit", component: <TemplateForm /> },
  { path: "/templates", component: <TemplatesList /> },
  { path: "/settings/sending", component: <SendingSetupPage /> },
  { path: "/settings/sending-controls", component: <SendingControlsPage /> },
  { path: "/settings/deliverability", component: <DeliverabilityHubPage /> },
  { path: "/settings/subscription", component: <SubscriptionSettingsPage /> },
  { path: "/settings/account", component: <AccountSettingsPage /> },
  { path: "/onboarding/google", component: <GoogleOnboarding /> },
  { path: "/settings/team", component: <TeamSettingsPage /> },
  { path: "/settings/workspace", component: <WorkspaceSettingsPage /> },
  { path: "/settings/referral", component: <ReferralSettingsPage /> },
  { path: "/settings/contact-fields", component: <ContactFieldsPage /> },
  { path: "/settings/api-tokens", component: <ApiTokensPage /> },
  { path: "/settings/webhooks", component: <WebhookList /> },
  { path: "/settings/webhooks/new", component: <WebhookCreateEdit /> },
  { path: "/settings/webhooks/:id", component: <WebhookCreateEdit /> },
  { path: "/integrations/vtex", component: <VtexIntegrationPage /> },
  { path: "/integrations/shopify", component: <ShopifyIntegrationPage /> },
  { path: "/integrations/uappi", component: <UappiIntegrationPage /> },
  { path: "/integrations/site-pixel", component: <SitePixelPage /> },
  { path: "/integrations", component: <IntegrationsHub /> },
  { path: "/suspended", component: <SuspendedBillingPage /> },
  // Alias legado → canônico DOC 09
  { path: "/sending-setup", component: <Navigate to="/settings/sending" replace /> },

  {
    path: "/",
    exact: true,
    component: <Navigate to="/dashboard" />,
  },
  // 404 no shell (não redirecionar em silêncio — anti-viés LAC-011)
  { path: "*", component: <NotFound homeTo="/dashboard" /> },
];

const publicRoutes = [
  { path: "/logout", component: <Logout /> },
  { path: "/login", component: <Login /> },
  { path: "/login/mfa", component: <LoginMfa /> },
  { path: "/forgot-password", component: <ForgetPasswordPage /> },
  { path: "/register", component: <Register /> },
  { path: "/accept-invite", component: <AcceptInvite /> },
  { path: "/team/invites/accept", component: <AcceptInvite /> },
  // LAC-074: SA cria tenant com convite → link do e-mail.
  { path: "/owner-invite/accept", component: <AcceptOwnerInvite /> },
  { path: "/unsubscribe", component: <PublicUnsubscribe /> },
  { path: "/super-admin/login", component: <SuperAdminLogin /> },
];

/** Rotas autenticadas do Super Admin — guard + layout próprios. */
const superAdminProtectedRoutes = [
  { path: "/super-admin", component: <Navigate to="/super-admin/dashboard" replace /> },
  { path: "/super-admin/dashboard", component: <SuperAdminDashboard /> },
  { path: "/super-admin/account", component: <SuperAdminAccount /> },
  { path: "/super-admin/sending", component: <SuperAdminSending /> },
  { path: "/super-admin/reputation", component: <SuperAdminReputation /> },
  { path: "/super-admin/ses-webhooks", component: <SuperAdminSesWebhooks /> },
  { path: "/super-admin/inactive-ranking", component: <SuperAdminInactiveRanking /> },
  { path: "/super-admin/ses/endpoints", component: <SuperAdminSesEndpoints /> },
  { path: "/super-admin/ses/setup-guide", component: <SuperAdminSesSetupGuide /> },
  { path: "/super-admin/ses/byo-guide", component: <SuperAdminSesByoGuide /> },
  { path: "/super-admin/dns/ns-guide", component: <SuperAdminDnsNsGuide /> },
  { path: "/super-admin/activity-logs", component: <SuperAdminActivityLogs /> },
  { path: "/super-admin/legacy-migration", component: <SuperAdminLegacyMigration /> },
  { path: "/super-admin/email-suppressions", component: <SuperAdminEmailSuppressions /> },
  { path: "/super-admin/domain-suppressions", component: <SuperAdminDomainSuppressions /> },
  { path: "/super-admin/tenants/new", component: <SuperAdminTenantNew /> },
  { path: "/super-admin/tenants/:id", component: <SuperAdminTenantDetail /> },
  { path: "/super-admin/tenants", component: <SuperAdminTenantsList /> },
  { path: "/super-admin/plans", component: <SuperAdminPlansList /> },
  { path: "/super-admin/coupons", component: <SuperAdminCouponsList /> },
  { path: "/super-admin/billing/paypal", component: <SuperAdminPayPalSettings /> },
  { path: "/super-admin/platform-templates/new", component: <PlatformTemplateForm /> },
  { path: "/super-admin/platform-templates/:id/edit", component: <PlatformTemplateForm /> },
  { path: "/super-admin/platform-templates", component: <PlatformTemplatesList /> },
  { path: "/super-admin/*", component: <NotFound homeTo="/super-admin/dashboard" /> },
];

export { authProtectedRoutes, publicRoutes, superAdminProtectedRoutes };
